'use client';
import styles from '@/styles/Menu.module.scss';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { BoxArrowUpRight, ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import slugify from 'slugify';
import { LicenseInfo } from './Policy';
import { Collapse, Nav, NavItem, NavLink } from "./wrapped";

type RoutedNavLinkProps = {
  href: string
  current?: string
  active?: boolean
}

// Strip query parameters for path comparison
function normalizePath(path: string): string {
  return path.split('?')[0];
}

export function RoutedNavLink({ href, current, active, children }: React.PropsWithChildren<RoutedNavLinkProps>) {
  return (
    <NavLink href={href} active={current === normalizePath(href)}>{children}</NavLink>
  )
}

type MenuItem = {
  href?: string;
  label: ReactNode;
  nestedItems?: MenuItem[];
};

type CollapsibleMenuSectionProps = {
  // The item rendered as the header for this collapsible section or subsection;
  // may or may not be a page in its own right (depends on whether `href` is provided)
  headingItem: MenuItem;
  // The items of this section or subsection
  items: MenuItem[];
  // The current URL path, e.g. '/reference/'; used to auto-expand sections and subsections
  // containing the current page, and to set `active` on the RoutedNavLink
  currentPath: string;
  // True when this section is rendered inside another CollapsibleMenuSection;
  // renders a plain <div> root instead of <NavItem> to avoid invalid nesting
  isSubSection?: boolean;
  // Seed the section open even when the current page isn't inside it (the user
  // can still collapse it). Use for a section that is the point of its menu.
  defaultExpanded?: boolean;
};

// Recursively checks whether any of the given items (or their nested items) matches `path`;
// used to expand or highlight the correct menu headings
function itemsContainPath(items: MenuItem[], path: string): boolean {
  return items.some(item =>
    (item.href !== undefined && normalizePath(item.href) === path) ||
    (item.nestedItems ? itemsContainPath(item.nestedItems, path) : false)
  );
}

// Finds the href of the first navigable item (descending into nested sections),
// used to route a heading that isn't a page in its own right to its first child
function firstItemHref(items: MenuItem[]): string | undefined {
  for (const item of items) {
    if (item.href !== undefined) return item.href;
    if (item.nestedItems) {
      const nested = firstItemHref(item.nestedItems);
      if (nested !== undefined) return nested;
    }
  }
  return undefined;
}

function CollapsibleMenuSection({ headingItem, items, currentPath, isSubSection, defaultExpanded }: CollapsibleMenuSectionProps) {
  const normalizedPath = normalizePath(currentPath);
  const headingPath = normalizePath(headingItem.href ?? '');
  // check the nested items recursively for the current path
  const currentInItems = itemsContainPath(items, normalizedPath);
  const hasItems = items.length > 0;

  // Determine if the section should be expanded by default, i.e. if currentPath is anywhere in the section (including the heading), or the caller asked for it
  const isExpandedByDefault = defaultExpanded === true || normalizedPath === headingPath || currentInItems;

  const [isExpanded, setIsExpanded] = useState<boolean>(isExpandedByDefault);

  const toggleExpanded = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Point out the heading when an item is active but the heading is collapsed
  const pointOutHeading = currentInItems && !isExpanded;

  // When the heading isn't a page itself, clicking it navigates to the first item
  const fallbackHref = firstItemHref(items);

  // Each section or subsection heading is one of:
  //  - a link to its own page (RoutedNavLink, with active highlighting)
  //  - a link to its first item, when it has no page of its own. We navigate like any
  //    other menu link (the menu remounts per page, seeding this section open with no
  //    animation) and deliberately omit active detection so only the child highlights.
  //  - a plain togglable heading, when there's nothing to link to
  const headingNode = headingItem.href ? (
    <RoutedNavLink href={headingItem.href} current={normalizedPath}>{headingItem.label}</RoutedNavLink>
  ) : fallbackHref ? (
    <NavLink href={fallbackHref}>{headingItem.label}</NavLink>
  ) : (
    <span className={classNames('nav-link', styles.menuItemLabel)} onClick={toggleExpanded} style={{ cursor: 'pointer' }}>{headingItem.label}</span>
  );

  // Stable unique ID for aria-controls derived from href or label (avoids SSR/client hydration mismatch)
  const slug = slugify(headingItem.href ?? (typeof headingItem.label === 'string' ? headingItem.label : ''), { lower: true, strict: true });
  const collapseId = `menu-${slug}`;

  // render <NavItem>s for main sections and <div>s for subsections to avoid invalid nesting, overindentation, etc
  const Root: React.ElementType = isSubSection ? 'div' : NavItem;

  return (
    <Root>
      {/* Heading link with expand/collapse icon */}
      <div className={styles.menuItem}>
        {hasItems && (
          <button
            onClick={toggleExpanded}
            className={styles.menuItemChevron}
            title={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            aria-controls={collapseId}
            aria-label={isExpanded ? 'Collapse submenu' : 'Expand submenu'}
            type="button"
          >
            {isExpanded ? <ChevronDown /> : <ChevronRight />}
          </button>
        )}
        <span className={styles.menuItemHeading}>
          {pointOutHeading ? (
            <span className={styles.subtleActive}>
              {headingNode}
            </span>
          ) : (
            headingNode
          )}
        </span>
      </div>

      {/* Nested items with collapse animation */}
      <Collapse in={isExpanded}>
        <div id={collapseId}>
          <NavItem as="div">
            {items.map((item, idx) => (
              item.nestedItems ? (
                <CollapsibleMenuSection
                  key={item.href ?? idx}
                  headingItem={item}
                  items={item.nestedItems}
                  currentPath={normalizedPath}
                  isSubSection={true}
                />
              ) : item.href ? (
                <RoutedNavLink key={item.href} href={item.href} current={normalizedPath}>
                  {item.label}
                </RoutedNavLink>
              ) : null
            ))}
          </NavItem>
        </div>
      </Collapse>
    </Root>
  );
}

export type MenuProps = {
  path: string
}

export function AboutMenu({ path }: MenuProps) {
  return (
    <>
      <Nav className="flex-column justify-content-start d-print-none" variant="pills">
        <RoutedNavLink href="/docs/about/" current={path}>About OpenSanctions</RoutedNavLink>
        <RoutedNavLink href="/docs/delisting/" current={path}>De-listing</RoutedNavLink>
        <RoutedNavLink href="/docs/company/team/" current={path}>The team</RoutedNavLink>
        <RoutedNavLink href="/docs/company/jobs/" current={path}>Work with us</RoutedNavLink>
        <RoutedNavLink href="/docs/bibliography/" current={path}>Research bibliography</RoutedNavLink>
        <RoutedNavLink href="/docs/privacy/" current={path}>Privacy Policy</RoutedNavLink>
        <RoutedNavLink href="/impressum/" current={path}>Impressum</RoutedNavLink>
        <RoutedNavLink href="/contact/" current={path}>Contact us</RoutedNavLink>
      </Nav>
      <LicenseInfo />
    </>
  );
}

export function DocumentationMenu({ path }: MenuProps) {
  return (
    <>
      <Nav className="flex-column justify-content-start d-print-none" variant="pills">

        <CollapsibleMenuSection
          headingItem={{ href: "/docs/data/", label: "Understanding the data" }}
          currentPath={path}
          items={[
            { href: "/docs/entities/", label: "Entity structure" },
            { href: "/docs/nested-entities/", label: "Flat and nested entities" },
            { href: "/docs/identifiers/", label: "Identifiers and de-duplication" },
            { href: "/docs/enrichment/", label: "Data enrichment" },
            { href: "/docs/monitoring/", label: "Monitoring workflow" },
            { href: "/docs/data/changes/", label: "Change policy" },
          ]}
        />

        <CollapsibleMenuSection
          headingItem={{ href: "/docs/bulk/", label: "Bulk data service" }}
          currentPath={path}
          items={[
            { href: "/docs/bulk/formats/", label: "Data formats", nestedItems: [
              { href: "/docs/bulk/json/", label: "FollowTheMoney JSON" },
              { href: "/docs/bulk/csv/", label: "Simplified CSV" },
              { href: "/docs/bulk/plain/", label: "Plain text names" },
              { href: "/docs/bulk/senzing/", label: "Senzing" },
            ]},
            { href: "/docs/bulk/updates/", label: "Downloading the data", nestedItems: [
              { href: "/docs/bulk/delivery-service/", label: "The data delivery service" },
              { href: "/docs/bulk/delta/", label: "Using delta files" },
            ] },
            { href: "/docs/metadata/", label: "Dataset metadata" },
          ]}
        />

        <CollapsibleMenuSection
          headingItem={{ href: "/docs/api/", label: "API" }}
          currentPath={path}
          items={[
            { href: "/docs/api/authentication/", label: "Authentication" },
            { href: "/docs/api/matching/", label: 'The matching API', nestedItems: [
              { href: "/docs/api/quickstart/", label: "Quickstart tutorial" },
              { href: "/docs/api/request/", label: "The request" },
              { href: "/docs/api/response/", label: "The response" },
              { href: "/docs/api/tuning/", label: "Tuning the matching algorithm" },
              { href: "/matcher/", label: "Matching algorithms" },
            ]},
            { href: "/docs/api/entities/", label: "The entities API" },
            { href: "/docs/api/search/", label: "The search API" },
            { href: "/docs/api/faq/", label: "FAQ" },
          ]}
        />

        <CollapsibleMenuSection
          headingItem={{ label: "Reference" }}
          currentPath={path}
          items={[
            { href: "/reference/", label: "Data dictionary" },
            { href: "/docs/topics/", label: "Entity risk tagging" },
            { href: "/docs/programs/", label: "Sanctions programs" },
            { href: "/docs/statements/", label: "Statement-based data" },
            { href: "/docs/glossary/", label: "Glossary" },
          ]}
        />

        <CollapsibleMenuSection
          headingItem={{ href: "/docs/coverage/", label: "Specialized coverage" }}
          currentPath={path}
          items={[
            { href: "/docs/coverage/pep/", label: "Politically exposed persons", nestedItems: [
              { href: "/docs/coverage/pep/methodology/", label: "Methodology" },
              { href: "/docs/coverage/pep/using/", label: "Using PEP data" },
            ]},
            { href: "/docs/coverage/securities/", label: "Securities data" },
            { href: "/docs/coverage/maritime/", label: "Maritime and vessel data" },
            { href: "/docs/coverage/kyb/", label: "Know-Your-Business (KYB) data" },
            { href: "/docs/coverage/eu/", label: "EU sanctions" },
          ]}
        />

        <CollapsibleMenuSection
          headingItem={{ href: "/docs/opensource/", label: "Open source" }}
          currentPath={path}
          items={[
            { href: "https://yente.followthemoney.tech", label: <>yente <BoxArrowUpRight className="menu-icon" /></> },
            { href: "https://yenteclient.followthemoney.tech", label: <>yente-client <BoxArrowUpRight className="menu-icon" /></> },
            { href: "https://followthemoney.tech", label: <>followthemoney <BoxArrowUpRight className="menu-icon" /></> },
            { href: "https://zavod.opensanctions.org", label: <>zavod <BoxArrowUpRight className="menu-icon" /></> },
            { href: "https://nomenklatura.followthemoney.tech", label: <>nomenklatura <BoxArrowUpRight className="menu-icon" /></> },
            { href: "/docs/opensource/fincrime/", label: "Open building blocks" },
            { href: "/docs/opensource/contributing/", label: "Contributing" },
            { href: "/docs/criteria/", label: "Data inclusion criteria" },
            { href: "/docs/opensource/pairs/", label: "Matcher training data" }
          ]}
        />

      </Nav>
      <LicenseInfo />
    </>
  );
}

export function ServicesMenu({ path }: MenuProps) {
  return (
    <Nav className="flex-column justify-content-start d-print-none" variant="pills">
      <RoutedNavLink href="/api/" current={path}>Screening API</RoutedNavLink>
      <CollapsibleMenuSection
        headingItem={{ href: "/licensing/", label: "Bulk data licensing" }}
        currentPath={path}
        defaultExpanded
        items={[
          { href: "/licensing/screening/", label: "Screening license" },
          { href: "/licensing/reseller/", label: "Reseller/OEM" },
        ]}
      />
      <RoutedNavLink href="/sales/" current={path}>Talk to sales</RoutedNavLink>
      <RoutedNavLink href="/support/" current={path}>Get support</RoutedNavLink>
      <RoutedNavLink href="/docs/on-premise/" current={path}>On-premise deployment</RoutedNavLink>
      <RoutedNavLink href="/docs/commercial/exemption/" current={path}>Free and non-commercial use</RoutedNavLink>
      <RoutedNavLink href="/docs/terms/" current={path}>Terms and conditions</RoutedNavLink>
      <RoutedNavLink href="/docs/commercial/faq/" current={path}>FAQ</RoutedNavLink>
    </Nav>
  );
}
